import { type FC, useState, useEffect } from 'react';
import { Store, ExternalLink, MapPin, Award, Heart } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, Badge } from '@/components/ui';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/Dialog';
import { supabase } from '@/lib/api/supabase';

interface Sponsor {
  id: string;
  name: string;
  description: string | null;
  logo_url: string | null;
  website_url: string | null;
  type: string;
  tier: string | null;
  booth_number: string | null;
  color: string | null;
  display_order: number | null;
  visible: boolean | null;
}

/**
 * Sponsors and Vendors page displaying them in card format with modal details
 */
export const SponsorsAndVendorsPage: FC = () => {
  const [sponsors, setSponsors] = useState<Sponsor[]>([]);
  const [vendors, setVendors] = useState<Sponsor[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState<Sponsor | null>(null);
  const [activeTab, setActiveTab] = useState<'sponsors' | 'vendors'>('sponsors');

  useEffect(() => {
    const fetchSponsorsAndVendors = async (): Promise<void> => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('sponsors')
          .select('*')
          .eq('visible', true)
          .order('display_order', { ascending: true });

        if (error) {
          console.error('Error fetching sponsors and vendors:', error);
          return;
        }

        if (data) {
          const sponsorsList = data.filter(item => item.type === 'sponsor');
          const vendorsList = data.filter(item => item.type === 'vendor');
          setSponsors(sponsorsList as Sponsor[]);
          setVendors(vendorsList as Sponsor[]);
        }
      } catch (err) {
        console.error('Failed to fetch sponsors and vendors:', err);
      } finally {
        setLoading(false);
      }
    };

    void fetchSponsorsAndVendors();
  }, []);

  const getBriefDescription = (description: string | null): string => {
    if (!description) return 'Click to view details';
    const firstSentence = description.split('.')[0];
    if (firstSentence && firstSentence.length <= 150) {
      return firstSentence + '.';
    }
    return description.substring(0, 147) + '...';
  };

  const getDefaultTierColor = (tier: string | null): string => {
    switch (tier) {
      case 'cholla':
        return '#c026d3'; // fuchsia-600
      case 'creosote':
        return '#65a30d'; // lime-600
      case 'opuntia':
        return '#f59e0b'; // amber-500
      case 'saguaro':
        return '#059669'; // emerald-600
      case 'ocotillo':
        return '#e11d48'; // rose-600
      default:
        return '#14b8a6'; // turquoise-500
    }
  };

  const getTierDisplayName = (tier: string | null): string => {
    switch (tier) {
      case 'cholla':
        return 'Cholla';
      case 'creosote':
        return 'Creosote';
      case 'opuntia':
        return 'Opuntia';
      case 'saguaro':
        return 'Saguaro';
      case 'ocotillo':
        return 'Ocotillo';
      default:
        return tier ? tier.charAt(0).toUpperCase() + tier.slice(1) : '';
    }
  };

  const renderCard = (item: Sponsor) => (
    <Card
      key={item.id}
      className="cursor-pointer hover:border-orange-500/60 transition-all hover:shadow-xl hover:scale-[1.02]"
      onClick={() => setSelectedItem(item)}
    >
      <CardHeader className="pb-3">
        {item.logo_url ? (
          <div className="w-full aspect-[4/3] rounded-lg overflow-hidden mb-4 bg-white p-4 flex items-center justify-center">
            <img
              src={item.logo_url}
              alt={item.name}
              className="w-full h-full object-contain"
            />
          </div>
        ) : (
          <div className="w-full aspect-[4/3] rounded-lg bg-turquoise-700/50 flex items-center justify-center mb-4">
            <Store className="h-16 w-16 text-turquoise-400" />
          </div>
        )}
        <CardTitle className="text-xl break-words line-clamp-2">{item.name}</CardTitle>
        {item.tier && item.type === 'sponsor' && (
          <Badge
            className="w-fit mt-2 text-white"
            style={{ backgroundColor: item.color || getDefaultTierColor(item.tier) }}
          >
            {getTierDisplayName(item.tier)} Sponsor
          </Badge>
        )}
      </CardHeader>
      <CardContent>
        <p className="text-sm text-white/80 mb-3 line-clamp-3">
          {getBriefDescription(item.description)}
        </p>
        {item.booth_number && (
          <div className="flex items-center gap-2 text-sm text-turquoise-300 mb-2">
            <MapPin className="h-4 w-4" />
            <span>Booth #{item.booth_number}</span>
          </div>
        )}
        <p className="text-xs text-orange-400 mt-3">Click to view details</p>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="bg-gradient-to-b from-turquoise-800 via-turquoise-700 to-turquoise-800 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-orange-500 border-r-transparent"></div>
          <p className="mt-4 text-white">Loading sponsors and vendors...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-b from-turquoise-800 via-turquoise-700 to-turquoise-800 min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[200px] flex items-center justify-center py-12">
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center text-white">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-viking mb-4 drop-shadow-lg">
              Sponsors & Vendors
            </h1>
            <p className="text-lg sm:text-xl font-viking drop-shadow-md">
              Thank you to our amazing supporters who make Cactus Cup possible!
            </p>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 py-12 max-w-6xl">
        {/* Become a Sponsor/Vendor Buttons */}
        <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4 mb-8 sm:mb-12">
          <a
            href="https://forms.gle/fAFHD9YsBHgn1jTA9"
            target="_blank"
            rel="noopener noreferrer"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-orange-500 hover:bg-orange-600 text-white font-viking text-base sm:text-lg rounded-lg shadow-lg hover:shadow-xl transition-all hover:scale-105"
          >
            <Heart className="h-5 w-5" />
            Become a Sponsor
          </a>
          <a
            href="https://forms.gle/Jtu3riFEkpyVoQKT7"
            target="_blank"
            rel="noopener noreferrer"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-turquoise-600 hover:bg-turquoise-500 text-white font-viking text-base sm:text-lg rounded-lg shadow-lg hover:shadow-xl transition-all hover:scale-105"
          >
            <Store className="h-5 w-5" />
            Become a Vendor
          </a>
        </div>

        {/* Tab Navigation */}
        <div className="flex justify-center mb-6 sm:mb-8">
          <div className="inline-flex rounded-lg bg-turquoise-900/50 p-1 backdrop-blur-sm">
            <button
              onClick={() => setActiveTab('sponsors')}
              className={`
                px-4 sm:px-8 py-2 sm:py-3 rounded-md font-viking text-base sm:text-lg font-semibold transition-all
                ${
                  activeTab === 'sponsors'
                    ? 'bg-orange-500 text-white shadow-lg'
                    : 'text-white hover:text-orange-300'
                }
              `}
            >
              Sponsors
              {sponsors.length > 0 && (
                <span className="ml-1 sm:ml-2 text-xs sm:text-sm">({sponsors.length})</span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('vendors')}
              className={`
                px-4 sm:px-8 py-2 sm:py-3 rounded-md font-viking text-base sm:text-lg font-semibold transition-all
                ${
                  activeTab === 'vendors'
                    ? 'bg-orange-500 text-white shadow-lg'
                    : 'text-white hover:text-orange-300'
                }
              `}
            >
              Vendors
              {vendors.length > 0 && (
                <span className="ml-1 sm:ml-2 text-xs sm:text-sm">({vendors.length})</span>
              )}
            </button>
          </div>
        </div>

        {/* Sponsors Tab Content */}
        {activeTab === 'sponsors' && (
          <>
            {sponsors.length > 0 ? (
              <section>
                {/* Cholla Sponsors - $1200+ */}
                {sponsors.filter(s => s.tier === 'cholla').length > 0 && (
                  <div className="mb-12">
                    <h3 className="text-3xl font-viking text-fuchsia-300 mb-2 text-center flex items-center justify-center gap-2">
                      <Award className="h-8 w-8" />
                      Cholla Sponsors
                    </h3>
                    <p className="text-center text-white/70 text-sm mb-6">$1,200+</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                      {sponsors.filter(s => s.tier === 'cholla').map(renderCard)}
                    </div>
                  </div>
                )}

                {/* Creosote Sponsors - $900-$1199 */}
                {sponsors.filter(s => s.tier === 'creosote').length > 0 && (
                  <div className="mb-12">
                    <h3 className="text-3xl font-viking text-lime-300 mb-2 text-center flex items-center justify-center gap-2">
                      <Award className="h-8 w-8" />
                      Creosote Sponsors
                    </h3>
                    <p className="text-center text-white/70 text-sm mb-6">$900 - $1,199</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                      {sponsors.filter(s => s.tier === 'creosote').map(renderCard)}
                    </div>
                  </div>
                )}

                {/* Opuntia Sponsors - $600-$899 */}
                {sponsors.filter(s => s.tier === 'opuntia').length > 0 && (
                  <div className="mb-12">
                    <h3 className="text-3xl font-viking text-amber-300 mb-2 text-center flex items-center justify-center gap-2">
                      <Award className="h-8 w-8" />
                      Opuntia Sponsors
                    </h3>
                    <p className="text-center text-white/70 text-sm mb-6">$600 - $899</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                      {sponsors.filter(s => s.tier === 'opuntia').map(renderCard)}
                    </div>
                  </div>
                )}

                {/* Saguaro Sponsors - $300-$599 */}
                {sponsors.filter(s => s.tier === 'saguaro').length > 0 && (
                  <div className="mb-12">
                    <h3 className="text-3xl font-viking text-emerald-300 mb-2 text-center flex items-center justify-center gap-2">
                      <Award className="h-8 w-8" />
                      Saguaro Sponsors
                    </h3>
                    <p className="text-center text-white/70 text-sm mb-6">$300 - $599</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                      {sponsors.filter(s => s.tier === 'saguaro').map(renderCard)}
                    </div>
                  </div>
                )}

                {/* Ocotillo Sponsors - $1-$299 */}
                {sponsors.filter(s => s.tier === 'ocotillo').length > 0 && (
                  <div className="mb-12">
                    <h3 className="text-3xl font-viking text-rose-300 mb-2 text-center flex items-center justify-center gap-2">
                      <Award className="h-8 w-8" />
                      Ocotillo Sponsors
                    </h3>
                    <p className="text-center text-white/70 text-sm mb-6">$1 - $299</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                      {sponsors.filter(s => s.tier === 'ocotillo').map(renderCard)}
                    </div>
                  </div>
                )}

                {/* Other Sponsors (no tier) */}
                {sponsors.filter(s => !s.tier).length > 0 && (
                  <div className="mb-12">
                    <h3 className="text-2xl sm:text-3xl font-viking text-white mb-4 sm:mb-6 text-center">Supporting Sponsors</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                      {sponsors.filter(s => !s.tier).map(renderCard)}
                    </div>
                  </div>
                )}
              </section>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <Award className="h-12 w-12 text-orange-500 mx-auto mb-4" />
                  <p className="text-white">No sponsors available at this time.</p>
                  <p className="text-white/70 text-sm mt-2">Check back soon for updates!</p>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* Vendors Tab Content */}
        {activeTab === 'vendors' && (
          <>
            {vendors.length > 0 ? (
              <section>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                  {vendors.map(renderCard)}
                </div>
              </section>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <Store className="h-12 w-12 text-orange-500 mx-auto mb-4" />
                  <p className="text-white">No vendors available at this time.</p>
                  <p className="text-white/70 text-sm mt-2">Check back soon for updates!</p>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>

      {/* Detail Modal */}
      <Dialog open={!!selectedItem} onOpenChange={() => setSelectedItem(null)}>
        <DialogContent className="w-[calc(100%-2rem)] max-w-2xl max-h-[90vh] overflow-y-auto bg-turquoise-800 border-2 border-orange-500 text-white p-0">
          {selectedItem && (
            <>
              {/* Hero Logo - Full width at top */}
              {selectedItem.logo_url && (
                <div className="w-full aspect-[16/9] overflow-hidden bg-white flex items-center justify-center p-8">
                  <img
                    src={selectedItem.logo_url}
                    alt={selectedItem.name}
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
              )}

              {/* Content area with padding */}
              <div className="p-4 sm:p-6 pb-6 sm:pb-8 space-y-4 sm:space-y-6">
                <DialogHeader className="p-0">
                  <DialogTitle className="text-2xl font-viking text-white pr-6 break-words">
                    {selectedItem.name}
                  </DialogTitle>
                  {selectedItem.tier && selectedItem.type === 'sponsor' && (
                    <DialogDescription className="text-orange-200 break-words">
                      {selectedItem.tier.charAt(0).toUpperCase() + selectedItem.tier.slice(1)} Sponsor
                    </DialogDescription>
                  )}
                </DialogHeader>

                {/* Description */}
                {selectedItem.description && (
                  <div className="overflow-hidden">
                    <h3 className="font-semibold text-white mb-2 flex items-center gap-2">
                      <Store className="h-4 w-4 text-orange-400" />
                      About
                    </h3>
                    <div className="text-sm text-orange-200 space-y-2 break-words max-h-48 overflow-y-auto pr-2 pb-2">
                      {selectedItem.description.split('\n\n').map((paragraph, idx) => (
                        <p key={idx}>{paragraph}</p>
                      ))}
                    </div>
                  </div>
                )}

                {/* Booth Number */}
                {selectedItem.booth_number && (
                  <div>
                    <h3 className="font-semibold text-white mb-2 flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-orange-400" />
                      Location
                    </h3>
                    <p className="text-sm text-orange-200">
                      Visit us at Booth #{selectedItem.booth_number}
                    </p>
                  </div>
                )}

                {/* Website */}
                {selectedItem.website_url && (
                  <div>
                    <h3 className="font-semibold text-white mb-2 flex items-center gap-2">
                      <ExternalLink className="h-4 w-4 text-orange-400" />
                      Website
                    </h3>
                    <a
                      href={selectedItem.website_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-orange-400 hover:text-orange-300 underline"
                    >
                      {selectedItem.website_url}
                    </a>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
